/**
 * App Context Factory
 * Creates unified context for web and mobile with shared dependencies
 */

import { createFrontendClient, forwardCall, forwardCallByName } from 'workers-common'
import { buildRootCategory, createToolAgent, MYAGENT_BOT_ID, MYAGENT_DEFAULT_CONFIG, buildBotSystemPrompt } from 'myagent_core'
import { createEmbed } from 'llm_sdk/unify/embedding'
import { createSemanticSearch } from 'llm_sdk/utils/semantic-search'
import createLlmSdk from 'llm_sdk'
import { createSessionManager } from 'session-manager'
import { createNotesManager } from 'myagent_notes'
import { AuthManager } from '../auth/index.js'
import { createApiService, createLlmConfigService, createSessionConfigService } from '../services/index.js'
import { createBotConfigService, createPeerService, createPeerFromWorker } from 'peer-sdk'
import { getPrivateKey, BrowserStore } from '../utils/index.js'

// Notes worker public key for tool configuration (myagent-worker handles notes)
const NOTES_PUBLIC_KEY = '5a67f1b192b85c868a3e233346cf316ae2665a0d09874eb69118c8742ac93aba'

/**
 * Create app context with all shared dependencies
 * @param {Object} options
 * @param {Object} options.storageAdapter - Platform-specific storage adapter (LocalStorageAdapter or CapacitorStorageAdapter)
 * @returns {Object} { auth, getClient, api, llmConfig, sessionConfig, botConfig, peerService, roleCall }
 */
// Decode JWT sub for consistent userId across local/remote modes
const withLocalAuth = (fn) => async (ctx, ...args) => {
  const authHeader = ctx.headers?.authorization
  if (authHeader?.startsWith('Bearer ')) {
    try {
      const sub = JSON.parse(atob(authHeader.split('.')[1])).sub
      if (sub) ctx.auth_info = { sub }
    } catch {}
  }
  return fn(ctx, ...args)
}

// Guard: only allow sandbox calls in local mode
const withLocalOnly = (sessionConfig, fn) => async (ctx, ...args) => {
  const isLocal = await sessionConfig.isLocalMode()
  if (!isLocal) throw new Error('Sandbox not available in cloud mode')
  return fn(ctx, ...args)
}

export function createAppContext({ storageAdapter }) {
  // Initialize auth manager
  const auth = new AuthManager(storageAdapter)

  // Initialize local storage
  const browserStore = new BrowserStore(storageAdapter)

  // Create session config service (local vs remote data storage)
  const sessionConfig = createSessionConfigService(storageAdapter)

  // Create embedding function using transformers.js (browser-native)
  const embed = createEmbed(null) // httpRequest not needed for transformers
  const embedFn = async (texts) => {
    return await embed({ texts, provider: 'transformers' })
  }
  const searcher = createSemanticSearch({ embedFn })

  // Sandbox guard: local mode + JWT auth
  const localSandbox = (fn) => withLocalOnly(sessionConfig, withLocalAuth(fn))

  // Local session sandbox
  const sessionManager = createSessionManager(browserStore)
  const sessionSandbox = Object.fromEntries(
    Object.entries(sessionManager.sandbox).map(([k, fn]) => [`session.${k}`, localSandbox(fn)])
  )

  // Local notes sandbox (no apiKeys/llmSdk — auto-metadata falls back to simple extraction)
  const notesManager = createNotesManager({ store: browserStore, searcher })
  const notesSandbox = Object.fromEntries(
    Object.entries(notesManager.sandbox).map(([k, fn]) => [`notes.${k}`, localSandbox(fn)])
  )

  // Initialize client (singleton)
  let clientInstance = null

  const getClient = () => {
    if (!clientInstance) {
      clientInstance = createFrontendClient({
        getPrivateKey,
        getAccessToken: async () => await auth.getValidAccessToken(),
        sandbox: { ...sessionSandbox, ...notesSandbox },
      })
      auth.setClient(clientInstance)
    }
    return clientInstance
  }

  // Build ctx with authorization header for tool execution
  // Workers will verify the token using Auth middleware
  const buildCtx = async () => {
    const token = await auth.getValidAccessToken()

    return {
      headers: {
        authorization: token ? `Bearer ${token}` : undefined,
      },
    }
  }

  // Initialize tools — notes route to 'local' or 'myagent-worker' based on config
  const getDataTarget = async () => {
    const isLocal = await sessionConfig.isLocalMode()
    return isLocal ? 'local' : 'myagent-worker'
  }

  const rootCategory = buildRootCategory({
    getWorkerClient: getClient,
    categoryConfig: {
      notes: { publicKey: NOTES_PUBLIC_KEY, getTarget: getDataTarget },
      searcher,
    },
  })

  // Create LLM config service (for user API key management)
  const llmConfig = createLlmConfigService(storageAdapter)

  // Create bot config service (custom LLM bot management)
  const botConfig = createBotConfigService(storageAdapter, {
    storageKey: 'myagent_bots',
    builtInBots: [MYAGENT_DEFAULT_CONFIG],
  })

  // Create frontend LLM SDK factory (for mobile direct mode)
  let frontendLlmSdk = null
  const getFrontendLlmSdk = () => {
    if (!frontendLlmSdk) {
      const { request } = require('platform-sdk/http/browser')
      frontendLlmSdk = createLlmSdk({ httpClient: request })
    }
    return frontendLlmSdk
  }

  // Create API service (unified chat routing handled inside)
  const api = createApiService({
    workerName: 'myagent-worker',
    getClient,
    getSessionTarget: getDataTarget,
    llmConfig,
    getFrontendLlmSdk,
  })

  // Discover remote roles via user-worker-registry (cached with 30s TTL)
  let rolesCache = null
  let rolesCacheTime = 0
  const ROLES_CACHE_TTL = 30000

  const discoverRoles = async ({ forceRefresh = false } = {}) => {
    const now = Date.now()
    if (!forceRefresh && rolesCache && (now - rolesCacheTime) < ROLES_CACHE_TTL) return rolesCache

    const ctx = await buildCtx()
    const client = getClient()
    const result = await forwardCallByName(ctx, client, 'user-worker-registry', 'list', [{}])

    const isHex = (s) => s && s.length >= 10 && /^[0-9a-f]+$/i.test(s)
    const roles = (result?.bindings || []).map(w => ({
      roleId: w.workerPublicKey,
      workerType: w.workerType,
      roleType: w.metadata?.roleType || 'tool',
      displayName: w.metadata?.name || (w.deviceName && !isHex(w.deviceName) ? w.deviceName : null) || w.workerType,
      typeName: w.metadata?.name || w.workerType,
      description: w.metadata?.description || '',
      online: !!w.online,
    }))

    rolesCache = roles
    rolesCacheTime = now
    return roles
  }

  // Direct RPC call to a role by publicKey (for AsyncTaskTracker polling)
  const roleCall = async (roleId, method, params) => {
    const ctx = await buildCtx()
    return forwardCall(ctx, getClient(), roleId, method, params)
  }

  // Get online non-chat workers for tool agent absorption (vault, trading bot, etc.)
  const getWorkerToolPeers = () => {
    if (!rolesCache) return []
    return rolesCache
      .filter(r => r.roleType !== 'chat' && r.online)
      .map(r => createPeerFromWorker(r))
  }

  // Create tool agent (reuses rootCategory, absorbs tool-type workers)
  const toolAgent = createToolAgent({
    rootCategory,
    buildCtx,
    getWorkerToolPeers,
    roleCall,
    llmCall: async (messages, tools) => {
      const [myagentBot, globalCfg] = await Promise.all([botConfig.getBot(MYAGENT_BOT_ID), llmConfig.getConfig()])
      const botCfg = globalCfg.toolAgentModel
        ? { ...myagentBot, llm: { ...myagentBot.llm, model: globalCfg.toolAgentModel } }
        : myagentBot
      return api.chatSingleStep({ messages, tools, botConfig: botCfg })
    },
  })

  // Delete a role binding from user-worker-registry
  const deleteRole = async (roleId) => {
    const ctx = await buildCtx()
    await forwardCallByName(ctx, getClient(), 'user-worker-registry', 'unbind', [{ workerPublicKey: roleId }])
    rolesCache = null
  }

  // Create peer service (unified peer interactions + chat engine + tool agent)
  const peerService = createPeerService({
    botConfigService: botConfig, discoverRoles, roleCall, deleteRole,
    chatFn: (messages, botCfg) => api.chat({ messages }, botCfg),
    buildPrompt: buildBotSystemPrompt,
    toolAgent,
  })

  return {
    auth,
    getClient,
    api,
    llmConfig,
    sessionConfig,
    botConfig,
    peerService,
    roleCall,
  }
}
